import { useAuth0 } from '@auth0/auth0-react';
import { useMemo, useEffect, useCallback } from 'react';
import spotlightService from '../services/SpotlightService';
import { userDataService } from '../services/UserDataService';
import { cacheService } from '../services/CacheService';
import profileService from '../services/ProfileService';

export const useStreamAuth = () => {
  const { user, isAuthenticated, isLoading, loginWithRedirect, logout: auth0Logout } = useAuth0();

  // Centralized logout function that clears all localStorage data
  const logout = useCallback(
    (options = {}) => {
      console.log('🔐 useStreamAuth: Logging out and clearing all user data...');
      
      // Clear all critical localStorage data
      cacheService.clearAllCriticalData();

      // Clear profile service data
      profileService.clearAllData();

      // Clear user data service
      userDataService.clearCurrentUser();

      // Clear SpotlightService data
      spotlightService.clearCurrentUser();
      spotlightService.clearCurrentProfileToken();
      spotlightService.clearSubscriptionCaches();

      // Clear user-specific localStorage items that might persist across accounts
      const userSpecificKeys = [
        'chillfree_payment_profile',
        'chillfree_user_purchases',
        'chillfree_subscription_status',
        'payment_profile_cache',
        'user_purchases_cache',
      ];
      
      userSpecificKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          console.log(`🔐 useStreamAuth: Cleared localStorage key: ${key}`);
        } catch (e) {
          console.warn(`🔐 useStreamAuth: Failed to clear ${key}:`, e);
        }
      });

      // Also clear any keys that start with user-specific prefixes
      try {
        const allKeys = Object.keys(localStorage);
        allKeys.forEach(key => {
          if (
            key.includes('payment_profile') ||
            key.includes('user_purchase') ||
            key.includes('subscription_check') ||
            key.includes('SUBSCRIPTION_CHECK')
          ) {
            localStorage.removeItem(key);
            console.log(`🔐 useStreamAuth: Cleared localStorage key: ${key}`);
          }
        });
      } catch (e) {
        console.warn('🔐 useStreamAuth: Error clearing localStorage:', e);
      }

      // Only call Auth0 logout when Auth0 is actually configured for this tenant —
      // otherwise the SDK redirects to https://undefined/v2/logout.
      if (process.env.REACT_APP_AUTH0_DOMAIN) {
        console.log('🔐 useStreamAuth: All user data cleared, calling Auth0 logout...');
        auth0Logout(options);
      }
    },
    [auth0Logout],
  );

  // Set the current user context in services when user changes
  useEffect(() => {
    if (user) {
      if (!spotlightService.getCurrentUser()) {
        spotlightService.setCurrentUser(user);
      }

      // Fetch fresh user data when user is authenticated
      const fetchUserData = async () => {
        try {
          // Only fetch user data if we have a user
          if (user && user.email) {
            await userDataService.getWatchlist();
            await userDataService.getAllResumptionsData();
          }
        } catch (error) {
          console.error('useStreamAuth: Error fetching user data:', error);
        }
      };

      fetchUserData();
    } else {
      spotlightService.clearCurrentUser();
      userDataService.clearCurrentUser();
    }
  }, [user]);

  const userRolesSet = useMemo(() => {
    if (!user) {return new Set();}
    const roles = user['https://chillfree.com/roles'] || [];
    return new Set(roles);
  }, [user]);

  // Single hasRole function to avoid duplication
  const hasRole = useMemo(() => {
    return role => userRolesSet.has(role);
  }, [userRolesSet]);

  const permissions = useMemo(() => {
    return {
      canStream: hasRole('streamer') || hasRole('admin'),
      canViewPremium: isAuthenticated,
      canModerate: hasRole('admin') || hasRole('moderator'),
      canManageUsers: hasRole('admin'),
      isStreamer: hasRole('streamer'),
      isAdmin: hasRole('admin'),
      isModerator: hasRole('moderator'),
      isPremiumUser: hasRole('premium') || hasRole('streamer') || hasRole('admin'),
    };
  }, [hasRole, isAuthenticated]);

  const getUserPreferences = useMemo(() => {
    if (!user) {return {};}

    return {
      theme: user['https://chillfree.com/theme'] || 'dark',
      language: user['https://chillfree.com/language'] || 'en',
      notifications: user['https://chillfree.com/notifications'] !== false,
      autoplay: user['https://chillfree.com/autoplay'] !== false,
      quality: user['https://chillfree.com/quality'] || 'auto',
    };
  }, [user]);

  const getStreamingSettings = useMemo(() => {
    if (!permissions.canStream) {return null;}

    return {
      maxBitrate: hasRole('premium') ? 8000 : 4000,
      maxResolution: hasRole('premium') ? '1080p' : '720p',
      canUseCustomThumbnails: hasRole('premium') || hasRole('admin'),
      canScheduleStreams: hasRole('premium') || hasRole('admin'),
      maxStreamDuration: hasRole('premium') ? 0 : 4 * 60 * 60, // 4 hours for free users, unlimited for premium
    };
  }, [permissions.canStream, hasRole]);

  return {
    user,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
    permissions,
    userPreferences: getUserPreferences,
    streamingSettings: getStreamingSettings,
    hasRole,
  };
};
